import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { t } from '../i18n';
import { Icon } from '../icons';
import type { ConsoleApi, PlaceFilter } from '../lib/api';
import { hrefFor } from '../shell/route';
import { selectBase, surfaceClass } from '../styles';
import { Button, PageHeader, SegmentedControl } from '../ui';
import { statusLabel } from './format';
import { AppLabel, VerificationLabel } from './labels';

export const PAGE_SIZE = 25;
const FILTERS: readonly PlaceFilter[] = [
  'all',
  'ready',
  'unverified',
  'published',
  'unpublished',
  'hidden',
];

function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return debounced;
}

export function PlacesScreen({ api }: { api: ConsoleApi }) {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<PlaceFilter>('all');
  const [zoneId, setZoneId] = useState<number | null>(null);
  const [page, setPage] = useState(0);
  const term = useDebounced(search, 250);

  const zones = useQuery({ queryKey: ['zones', 'pilot'], queryFn: () => api.listPilotZones() });
  const places = useQuery({
    queryKey: ['places', term, filter, zoneId, page],
    queryFn: () => api.listPlaces({ search: term, filter, zoneId, page, pageSize: PAGE_SIZE }),
    placeholderData: keepPreviousData,
  });

  const total = places.data?.total ?? 0;
  const from = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const to = Math.min(total, (page + 1) * PAGE_SIZE);

  return (
    <>
      <PageHeader
        title={t('places.title')}
        subtitle={
          places.isSuccess
            ? t(total === 1 ? 'places.subtitle.one' : 'places.subtitle', { total })
            : undefined
        }
      />

      {/* Toolbar (ConsolaLugares): the search alone on the first row; filters left and
          Barrio right on the second. Both rows end at the table's edge. */}
      <div className="grid grid-cols-[minmax(0,1fr)] gap-3">
        <div className="grid">
          <label htmlFor="places-search" className="mb-2 text-sm font-medium">
            {t('places.search')}
          </label>
          {/* The field draws the focus ring, so the icon sits inside it. */}
          <div className="flex min-h-11 min-w-0 items-center gap-2.5 rounded-xl border border-field bg-surface px-3.5 text-muted focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-ink">
            <Icon name="search" />
            <input
              id="places-search"
              type="search"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(0);
              }}
              placeholder={t('places.searchPlaceholder')}
              className="min-h-[42px] min-w-0 flex-1 bg-transparent text-[15px] text-ink outline-none placeholder:text-muted max-[799px]:text-base"
            />
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <SegmentedControl
            label={t('places.filterLabel')}
            options={FILTERS}
            value={filter}
            optionLabel={(option) => t(`places.filter.${option}`)}
            onChange={(next) => {
              setFilter(next);
              setPage(0);
            }}
          />
          <div className="ml-auto flex items-center gap-2 max-[639px]:grid max-[639px]:w-full max-[639px]:gap-1.5">
            <label
              htmlFor="places-zone"
              className="text-sm text-muted max-[639px]:font-medium max-[639px]:text-ink"
            >
              {t('places.zone')}
            </label>
            <select
              id="places-zone"
              value={zoneId ?? ''}
              onChange={(event) => {
                setZoneId(event.target.value ? Number(event.target.value) : null);
                setPage(0);
              }}
              className={`${selectBase} max-[639px]:w-full`}
            >
              <option value="">{t('places.allZones')}</option>
              {zones.data?.map((zone) => (
                <option key={zone.id} value={zone.id}>
                  {zone.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {places.isPending ? (
        <p role="status" className="text-muted">
          {t('places.loading')}
        </p>
      ) : null}

      {places.isError ? (
        <div role="alert" className="grid justify-items-start gap-3">
          <p>{t('places.error')}</p>
          <Button onClick={() => void places.refetch()}>{t('common.retry')}</Button>
        </div>
      ) : null}

      {places.isSuccess && places.data.items.length === 0 ? (
        <p className={`p-5 text-muted ${surfaceClass}`}>{t('places.empty')}</p>
      ) : null}

      {places.isSuccess && places.data.items.length > 0 ? (
        <>
          {/* Phones: one card per place, every state readable without scrolling sideways */}
          <ul className="grid gap-3 min-[640px]:hidden" aria-label={t('places.title')}>
            {places.data.items.map((place) => (
              <li key={place.id}>
                <a
                  href={hrefFor({ name: 'place', id: place.id })}
                  className={`grid gap-1.5 px-4 py-3.5 hover:border-field ${surfaceClass}`}
                >
                  <span className="text-[15px] font-semibold">{place.name}</span>
                  <span className="text-sm text-muted">
                    {place.zoneName ?? t('places.noZone')} · {statusLabel(place.status)}
                  </span>
                  <span className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
                    <VerificationLabel facts={place.facts} />
                    <span aria-hidden="true" className="text-muted">
                      ·
                    </span>
                    <AppLabel isPublished={place.isPublished} hiddenUntil={place.hiddenUntil} />
                  </span>
                </a>
              </li>
            ))}
          </ul>

          <div
            className={`overflow-x-auto max-[639px]:hidden ${surfaceClass}`}
            role="region"
            aria-label={t('places.title')}
            tabIndex={0}
          >
            <table className="w-full min-w-[640px] border-collapse text-left text-[14.5px]">
              <caption className="sr-only">{t('places.title')}</caption>
              <thead className="text-[13px] text-muted">
                <tr>
                  {(['name', 'zone', 'status', 'verification', 'app'] as const).map((column) => (
                    <th
                      key={column}
                      scope="col"
                      className="border-b border-line px-4 py-3 font-semibold whitespace-nowrap"
                    >
                      {t(`places.column.${column}`)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {places.data.items.map((place) => (
                  <tr
                    key={place.id}
                    className="border-b border-line last:border-b-0 hover:bg-canvas"
                  >
                    <th scope="row" className="px-4 py-0.5 font-semibold">
                      <a
                        href={hrefFor({ name: 'place', id: place.id })}
                        className="flex min-h-12 items-center underline-offset-4 hover:underline"
                      >
                        {place.name}
                      </a>
                    </th>
                    <td className="px-4 py-3.5">{place.zoneName ?? t('places.noZone')}</td>
                    <td className="px-4 py-3.5">{statusLabel(place.status)}</td>
                    <td className="px-4 py-3.5">
                      <VerificationLabel facts={place.facts} />
                    </td>
                    <td className="px-4 py-3.5">
                      <AppLabel isPublished={place.isPublished} hiddenUntil={place.hiddenUntil} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-muted">{t('places.showing', { from, to, total })}</p>
            {total > PAGE_SIZE ? (
              <div className="flex gap-2">
                <Button disabled={page === 0} onClick={() => setPage((value) => value - 1)}>
                  {t('places.previous')}
                </Button>
                <Button disabled={to >= total} onClick={() => setPage((value) => value + 1)}>
                  {t('places.next')}
                </Button>
              </div>
            ) : null}
          </div>
        </>
      ) : null}
    </>
  );
}
